import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridEditorActions } from '../../GridEditorPresenter';
import { Lightbulb, Trash2, Plus, X, ExternalLink } from 'lucide-react';
import { Light, ElementAction } from '../../types';
import { useLuminaires } from '../../../../luminaire/hooks/useLuminaires';
import { Luminaire } from '../../../../luminaire/types/luminaire.types';
import { ResponseAPIWithPagination } from '../../../../../common/types/API.types';

interface LightManagerProps {
  elementId: string;
  hostId?: string; // hostId de la pole
  lights: Light[];
  lightTypes: Array<{ id: string; label: string; power: number }>;
  actions: Pick<GridEditorActions, 'addLight' | 'removeLight' | 'updateLight' | 'updateElement'>;
  isEditMode?: boolean;
}

export const LightManager: React.FC<LightManagerProps> = ({
  elementId,
  hostId,
  lights,
  lightTypes,
  actions,
  isEditMode = false
}) => {
  const navigate = useNavigate();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDevEui, setSelectedDevEui] = useState('');

  const { luminairesQuery } = useLuminaires({ pageSize: 200 });
  const luminaires: Luminaire[] =
    (luminairesQuery.data as ResponseAPIWithPagination<Luminaire> | undefined)?.data ?? [];

  const activeLights = (lights || []).filter(l => l.action !== ElementAction.TO_DELETE);
  const usedDevEuis = new Set(activeLights.map(l => l.zhagaDevui).filter(Boolean));
  const availableLuminaires = luminaires.filter(lum => !usedDevEuis.has(lum.lum_dev_eui));

  const getLightIcon = (type: string) => {
    switch (type) {
      case 'street-light': return '💡';
      case 'flood-light': return '🔦';
      case 'decorative': return '✨';
      default: return '💡';
    }
  };

  const handleAddLight = () => {
    const selected = luminaires.find(l => l.lum_dev_eui === selectedDevEui);
    if (!selected) return;

    const lightId = `light_${Date.now()}`;
    const lightToAdd: Light = {
      id: lightId,
      type: 'street-light' as const,
      name: selected.lum_name || `Luminaire-${selected.lum_dev_eui.slice(0, 8)}`,
      number: selected.lum_pole_number || '',
      zhagaDevui: selected.lum_dev_eui,
      power: 50,
      status: 'on',
      action: ElementAction.TO_CREATE,
      poleId: hostId || elementId,
    };

    const currentLights = lights || [];
    actions.updateElement(elementId, { lights: [...currentLights, lightToAdd] });

    setSelectedDevEui('');
    setShowAddForm(false);
  };

  const handleCancelAdd = () => {
    setSelectedDevEui('');
    setShowAddForm(false);
  };

  const handleDeleteLight = (lightId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce lampadaire ?')) {
      actions.removeLight(elementId, lightId);
    }
  };

  const handleViewLuminaire = (devEui?: string) => {
    if (!devEui) return;
    navigate(`/main/luminaires/${devEui}`);
  };


  return (
    <div className="border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-slate-900 flex items-center">
          <Lightbulb className="h-4 w-4 mr-1" />
          Lampadaires
        </h4>
        {isEditMode && (
          <button
            onClick={() => setShowAddForm(true)}
            className="text-xs px-2 py-1 h-6 border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1"
          >
            <Plus className="h-3 w-3" />
            Ajouter
          </button>
        )}
      </div>

      {/* Formulaire d'ajout de lampadaire */}
      {showAddForm && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-medium text-blue-900">Nouveau lampadaire</h5>
            <button
              onClick={handleCancelAdd}
              className="h-5 w-5 p-0 rounded hover:bg-blue-100 flex items-center justify-center"
            >
              <X className="h-3 w-3 text-blue-600" />
            </button>
          </div>

          <div className="space-y-2">
            <div>
              <label className="block text-xs text-blue-800 mb-1">Luminaire Zhaga *</label>
              <select
                value={selectedDevEui}
                onChange={(e) => setSelectedDevEui(e.target.value)}
                disabled={luminairesQuery.isLoading}
                className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
              >
                <option value="">
                  {luminairesQuery.isLoading
                    ? 'Chargement...'
                    : availableLuminaires.length === 0
                    ? 'Aucun luminaire disponible'
                    : 'Sélectionner un luminaire...'}
                </option>
                {availableLuminaires.map((lum) => (
                  <option key={lum.lum_id} value={lum.lum_dev_eui}>
                    {lum.lum_name} — {lum.lum_dev_eui}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleAddLight}
                disabled={!selectedDevEui}
                className="text-xs px-3 py-1 h-6 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ajouter
              </button>
              <button
                onClick={handleCancelAdd}
                className="text-xs px-3 py-1 h-6 border border-blue-300 rounded hover:bg-blue-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3 max-h-60 overflow-y-auto">
        {activeLights.map((light) => (
          <div key={light.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm">{getLightIcon(light.type)}</span>
                <span className="text-xs font-medium text-slate-700">
                  {light.name || 'Lampadaire sans nom'}
                </span>
              </div>
              {isEditMode && (
                <button
                  onClick={() => handleDeleteLight(light.id)}
                  className="h-6 w-6 p-1 rounded hover:bg-red-50 border border-red-200 bg-white flex items-center justify-center"
                  title="Supprimer ce lampadaire"
                >
                  <Trash2 className="h-3 w-3 text-red-600" />
                </button>
              )}
            </div>

            <div className="space-y-2">
              <div>
                <label className="block text-xs text-slate-600 mb-1">ZhagaDevUI</label>
                <div className="text-xs text-slate-800 px-2 py-1 bg-slate-100 rounded font-mono">
                  {light.zhagaDevui || 'Non défini'}
                </div>
              </div>

              {light.zhagaDevui && (
                <button
                  onClick={() => handleViewLuminaire(light.zhagaDevui)}
                  className="w-full mt-2 px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center justify-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Voir les détails du luminaire
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {(!lights || lights.length === 0) && (
        <p className="text-xs text-slate-500 text-center py-2">
          No lights installed
        </p>
      )}
    </div>
  );
};
